import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  Clock,
  CaretRight,
  CaretLeft,
  ArrowSquareOut,
  Lightbulb,
  ListBullets,
  NotePencil,
  CaretDown,
  CaretUp,
  X,
  MagnifyingGlass,
  Plus,
  SortAscending,
  DotsSixVertical
} from '@phosphor-icons/react';
import { useAuthStore } from '../stores/authStore';
import FloatingActionButtons from '../components/FloatingActionButtons';
import DiscoverModal from '../components/DiscoverModal';
import toast from 'react-hot-toast';

interface ContentItem {
  id: string;
  title: string;
  url: string;
  category: string;
  reading_time_minutes: number;
  summary?: string;
  key_concepts?: string[];
  takeaways?: string[];
  difficulty?: string;
}

interface LearningPathItem {
  id: string;
  content_item_id: string;
  order_index: number;
  status: 'not_started' | 'in_progress' | 'completed';
  notes?: string;
  time_spent_minutes?: number;
  completed_at?: string;
  content: ContentItem;
}

interface LearningPath {
  id: string;
  title: string;
  description: string;
  topic: string;
  difficulty_level: string;
  estimated_hours: number;
  status: string;
  progress_percentage: number;
  created_at: string;
  items: LearningPathItem[];
}

export default function LearningPathDetailPage() {
  const { pathId } = useParams<{ pathId: string }>();
  const { token } = useAuthStore();
  const navigate = useNavigate();

  const [path, setPath] = useState<LearningPath | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [libraryItems, setLibraryItems] = useState<ContentItem[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isAddingItems, setIsAddingItems] = useState(false);
  
  // Inline sticky note state
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteSaveStatus, setNoteSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const noteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Drag and drop state
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [isSorting, setIsSorting] = useState(false);

  // Resizable panel state
  const [sidebarWidth, setSidebarWidth] = useState(256); // 256px = w-64
  const [isResizing, setIsResizing] = useState(false);
  const minWidth = 200;
  const maxWidth = 400;

  // Helper function to capitalize first letter of a string
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Toggle note visibility from FAB
  useEffect(() => {
    const handleToggleNote = () => {
      setIsNoteExpanded(prev => !prev);
    };

    window.addEventListener('openNoteEditor', handleToggleNote as EventListener);
    return () => {
      window.removeEventListener('openNoteEditor', handleToggleNote as EventListener);
    };
  }, []);

  // Load note for current item when item changes
  useEffect(() => {
    const currentItem = path?.items?.[currentItemIndex];
    if (currentItem?.notes) {
      setNoteContent(currentItem.notes);
    } else {
      setNoteContent('');
    }
    setNoteSaveStatus('idle');
  }, [currentItemIndex, path]);

  // Auto-save note with debounce
  const saveNote = useCallback(async (content: string) => {
    const currentItem = path?.items?.[currentItemIndex];
    if (!currentItem || !token || !pathId) return;

    setIsSavingNote(true);
    try {
      const response = await fetch(`/api/learning-paths/${pathId}/items/${currentItem.id}/notes`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: content })
      });

      if (response.ok) {
        setNoteSaveStatus('saved');
        // Update local state
        setPath(prev => {
          if (!prev) return prev;
          const newItems = [...prev.items];
          newItems[currentItemIndex] = { ...newItems[currentItemIndex], notes: content };
          return { ...prev, items: newItems };
        });
        // Reset status after 2s
        setTimeout(() => setNoteSaveStatus('idle'), 2000);
      } else {
        setNoteSaveStatus('error');
      }
    } catch (error) {
      console.error('Failed to save note:', error);
      setNoteSaveStatus('error');
    } finally {
      setIsSavingNote(false);
    }
  }, [path, currentItemIndex, token, pathId]);

  // Debounced note change handler
  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setNoteContent(newContent);
    setNoteSaveStatus('idle');

    // Clear existing timeout
    if (noteTimeoutRef.current) {
      clearTimeout(noteTimeoutRef.current);
    }

    // Set new timeout for auto-save (1.5s after typing stops)
    noteTimeoutRef.current = setTimeout(() => {
      saveNote(newContent);
    }, 1500);
  };

  // Store current item index in sessionStorage for ChatWidget to access
  useEffect(() => {
    if (pathId && currentItemIndex !== undefined) {
      sessionStorage.setItem(`learning-path-${pathId}-current-index`, currentItemIndex.toString());
    }
  }, [pathId, currentItemIndex]);

  // Load learning path details
  const loadPathDetails = useCallback(async () => {
    if (!pathId || !token) return;

    try {
      setIsLoading(true);
      const response = await fetch(`/api/learning-paths/${pathId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPath(data.data);
        
        const items = data.data?.items || [];
        const nextIncompleteIndex = items.findIndex(
          (item: LearningPathItem) => item.status !== 'completed'
        );
        setCurrentItemIndex(nextIncompleteIndex >= 0 ? nextIncompleteIndex : 0);
      }
    } catch (error) {
      console.error('Failed to load learning path:', error);
    } finally {
      setIsLoading(false);
    }
  }, [pathId, token]);

  useEffect(() => {
    loadPathDetails();
  }, [loadPathDetails]);

  // Load library items for add item modal
  const loadLibraryItems = useCallback(async () => {
    if (!token) return;
    
    setLibraryLoading(true);
    try {
      const response = await fetch('/api/content', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter out items already in this path
        const existingIds = new Set(path?.items?.map(i => i.content_item_id) || []);
        const availableItems = (data.data || []).filter((item: ContentItem) => !existingIds.has(item.id));
        setLibraryItems(availableItems);
      }
    } catch (error) {
      console.error('Failed to load library items:', error);
    } finally {
      setLibraryLoading(false);
    }
  }, [token, path]);

  // Add selected items to path
  const handleAddItemsToPath = async () => {
    if (!token || !pathId || selectedItems.size === 0) return;
    
    setIsAddingItems(true);
    try {
      const response = await fetch(`/api/learning-paths/${pathId}/add-items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contentIds: Array.from(selectedItems) })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${data.data.added} item(s) to trail`);
        setShowAddItemModal(false);
        setSelectedItems(new Set());
        await loadPathDetails(); // Refresh path
      } else {
        toast.error('Failed to add items');
      }
    } catch (error) {
      console.error('Failed to add items:', error);
      toast.error('Failed to add items');
    } finally {
      setIsAddingItems(false);
    }
  };

  // Open add item modal
  const handleOpenAddItemModal = () => {
    setShowAddItemModal(true);
    setSelectedItems(new Set());
    setSearchQuery('');
    loadLibraryItems();
  };

  // Toggle item selection
  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Filter library items by search
  const filteredLibraryItems = libraryItems.filter(item => 
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    if (!draggedItemId || draggedItemId === targetItemId || !path) return;

    const items = [...path.items];
    const draggedIndex = items.findIndex(i => i.id === draggedItemId);
    const targetIndex = items.findIndex(i => i.id === targetItemId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder locally first for instant feedback
    const [draggedItem] = items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, draggedItem);
    
    setPath({ ...path, items });
    setDraggedItemId(null);

    // Persist to backend
    try {
      const itemIds = items.map(i => i.id);
      await fetch(`/api/learning-paths/${pathId}/reorder`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ itemIds })
      });
    } catch (error) {
      console.error('Failed to save order:', error);
      toast.error('Failed to save order');
      loadPathDetails(); // Revert on error
    }
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  // Sort by difficulty handler
  const handleSortByDifficulty = async () => {
    if (!token || !pathId || isSorting) return;

    setIsSorting(true);
    try {
      const response = await fetch(`/api/learning-paths/${pathId}/sort-by-difficulty`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Sorted by difficulty');
        await loadPathDetails();
      } else {
        toast.error('Failed to sort');
      }
    } catch (error) {
      console.error('Failed to sort:', error);
      toast.error('Failed to sort');
    } finally {
      setIsSorting(false);
    }
  };

  // Resize handlers for sidebar
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, e.clientX));
      setSidebarWidth(newWidth);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const markAsComplete = async (itemId: string) => {
    if (!token || isUpdatingProgress) return;

    try {
      setIsUpdatingProgress(true);
      const response = await fetch(`/api/learning-paths/${pathId}/items/${itemId}/progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'completed' })
      });

      if (response.ok) {
        // Reload path data to get updated progress
        await loadPathDetails();
        
        // Move to next incomplete item, or stay on last item if all complete
        if (path && currentItemIndex < path.items.length - 1) {
          // Find the next incomplete item starting from current position
          const nextIncompleteIndex = path.items.findIndex(
            (item, idx) => idx > currentItemIndex && item.status !== 'completed'
          );
          
          // If found, move to it; otherwise stay on current (which is now complete)
          if (nextIncompleteIndex >= 0) {
            setCurrentItemIndex(nextIncompleteIndex);
          }
        }
      }
    } catch (error) {
      console.error('Failed to mark item as complete:', error);
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  const getDifficultyStyle = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'intermediate':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'advanced':
        return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-0px)] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-3 h-3 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_infinite]" />
            <span className="w-3 h-3 rounded-full bg-blue-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
            <span className="w-3 h-3 rounded-full bg-blue-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
          </div>
          <p className="text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!path) {
    return (
      <div className="h-[calc(100vh-0px)] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">Trail not found</p>
          <button
            onClick={() => navigate('/learning-paths')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Back to Knowledge Hub
          </button>
        </div>
      </div>
    );
  }

  const currentItem = path.items[currentItemIndex];
  const completedCount = path.items.filter(i => i.status === 'completed').length;
  const isCurrentCompleted = currentItem?.status === 'completed';

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Compact Header */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/learning-paths')}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft weight="bold" className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {path.title}
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getDifficultyStyle(path.difficulty_level)}`}>
                  {path.difficulty_level}
                </span>
                <span>•</span>
                <span>{path.estimated_hours}h</span>
                <span>•</span>
                <span>{completedCount}/{path.items.length} done</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{path.progress_percentage}%</span>
            <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div style={{ width: `${path.progress_percentage}%` }} className="h-full bg-blue-500 rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Timeline (Always Visible) */}
        <div 
          style={{ width: sidebarWidth }} 
          className="flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col"
        >
          {/* Timeline Header */}
          <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Topics
            </span>
            <button
              onClick={handleSortByDifficulty}
              disabled={isSorting}
              className="p-1.5 bg-violet-100 dark:bg-violet-900/30 hover:bg-violet-200 dark:hover:bg-violet-800/40 rounded-md transition-colors disabled:opacity-50"
              title="Sort by difficulty (beginner → advanced)"
            >
              {isSorting ? (
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-[bounce_1s_ease-in-out_infinite]" /><span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} /></span>
              ) : (
                <SortAscending weight="bold" className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              )}
            </button>
          </div>

          {/* Timeline Items */}
          <div className="flex-1 overflow-y-auto py-1">
            {path.items.map((item, index) => {
              const isActive = index === currentItemIndex;
              const isCompleted = item.status === 'completed';
              const isDragging = draggedItemId === item.id;

              return (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, item.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => setCurrentItemIndex(index)}
                  className={`w-full flex items-center gap-1 px-2 py-2 transition-all ${
                    isDragging ? 'opacity-50 bg-blue-100 dark:bg-blue-900/30 cursor-grabbing' :
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-2 border-transparent'
                  }`}
                >
                  {/* Drag Handle */}
                  <DotsSixVertical 
                    weight="bold" 
                    className={`w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 ${
                      isDragging ? 'cursor-grabbing' : 'cursor-grab'
                    }`}
                  />

                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    {isCompleted ? (
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500" />
                    ) : isActive ? (
                      <div className="w-4 h-4 rounded-full border-2 border-blue-500 bg-blue-500/20" />
                    ) : (
                      <Circle weight="regular" className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                    )}
                  </div>

                  {/* Title */}
                  <span className={`text-xs truncate text-left ${
                    isCompleted
                      ? 'text-gray-400 dark:text-gray-500 line-through'
                      : isActive
                      ? 'text-blue-600 dark:text-blue-400 font-medium'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {item.content?.title || 'Untitled'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Resize Handle */}
        <div
          onMouseDown={handleResizeStart}
          className={`w-1 hover:w-1.5 bg-transparent hover:bg-violet-400 dark:hover:bg-violet-500 cursor-col-resize transition-all flex-shrink-0 ${
            isResizing ? 'w-1.5 bg-violet-500' : ''
          }`}
        />

        {/* Right Panel - Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {currentItem ? (
            <>
              {/* Content Area - Scrollable if needed */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-2xl mx-auto">
                  {/* Content Header */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {currentItemIndex + 1} of {path.items.length}
                      </span>
                      {currentItem.content?.difficulty && (
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getDifficultyStyle(currentItem.content.difficulty)}`}>
                          {currentItem.content.difficulty}
                        </span>
                      )}
                      {isCurrentCompleted && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                          <CheckCircle weight="fill" className="w-3 h-3" />
                          Done
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                      {currentItem.content?.title || 'Untitled'}
                    </h2>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <Clock weight="fill" className="w-3 h-3" />
                      <span>{currentItem.content?.reading_time_minutes || 10} min</span>
                      {currentItem.content?.category && (
                        <>
                          <span>•</span>
                          <span>{currentItem.content.category}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb weight="duotone" className="w-4 h-4 text-amber-500" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Summary</h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {currentItem.content?.summary || 'No summary available. Read the full article to learn more.'}
                    </p>
                  </div>

                  {/* Key Takeaways */}
                  {(currentItem.content?.takeaways?.length || currentItem.content?.key_concepts?.length) ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ListBullets weight="duotone" className="w-4 h-4 text-blue-500" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Key Takeaways</h3>
                      </div>
                      <ul className="space-y-1.5">
                        {(currentItem.content?.takeaways || currentItem.content?.key_concepts || []).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <span className="text-blue-500 mt-0.5">•</span>
                            <span>{capitalizeFirstLetter(item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {/* Sticky Note Section */}
                  <div className="mt-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      onClick={() => setIsNoteExpanded(!isNoteExpanded)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <NotePencil weight="duotone" className="w-4 h-4 text-amber-500" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">My Notes</h3>
                        {noteContent && !isNoteExpanded && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                            ({noteContent.length} chars)
                          </span>
                        )}
                        {noteSaveStatus === 'saved' && (
                          <span className="text-xs text-emerald-500 flex items-center gap-1">
                            <CheckCircle weight="fill" className="w-3 h-3" />
                            Saved
                          </span>
                        )}
                        {isSavingNote && (
                          <span className="text-xs text-gray-400">Saving...</span>
                        )}
                      </div>
                      {isNoteExpanded ? (
                        <CaretUp weight="bold" className="w-4 h-4 text-gray-400" />
                      ) : (
                        <CaretDown weight="bold" className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    
                    {isNoteExpanded && (
                      <div className="px-4 pb-4">
                        <textarea
                          value={noteContent}
                          onChange={handleNoteChange}
                          placeholder="Write your notes here... (auto-saves as you type)"
                          className="w-full h-40 p-3 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 
                                   rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                   text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                        />
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                          Notes are saved automatically and linked to this item.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Action Bar - Fixed */}
              <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
                <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
                  {/* Prev/Next */}
                  <button
                    onClick={() => setCurrentItemIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentItemIndex === 0}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 
                             hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <CaretLeft weight="bold" className="w-4 h-4" />
                    <span className="hidden sm:inline">Prev</span>
                  </button>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {currentItem.content?.url && (
                      <a
                        href={currentItem.content.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 
                                 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors 
                                 flex items-center gap-1.5"
                      >
                        <ArrowSquareOut weight="bold" className="w-4 h-4" />
                        <span className="hidden sm:inline">Read Full</span>
                      </a>
                    )}
                    {!isCurrentCompleted && (
                      <button
                        onClick={() => markAsComplete(currentItem.id)}
                        disabled={isUpdatingProgress}
                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm 
                                 font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {isUpdatingProgress ? (
                          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} /></span>
                        ) : (
                          <CheckCircle weight="bold" className="w-4 h-4" />
                        )}
                        <span>Got It!</span>
                      </button>
                    )}
                  </div>

                  {/* Next */}
                  <button
                    onClick={() => setCurrentItemIndex(prev => Math.min(path.items.length - 1, prev + 1))}
                    disabled={currentItemIndex === path.items.length - 1}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 
                             hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <CaretRight weight="bold" className="w-4 h-4" />
                  </button>
                </div>

                {/* Progress Dots */}
                <div className="flex items-center justify-center gap-1 mt-2">
                  {path.items.map((item, index) => {
                    const isActive = index === currentItemIndex;
                    const isCompleted = item.status === 'completed';
                    return (
                      <button
                        key={item.id}
                        onClick={() => setCurrentItemIndex(index)}
                        className={`h-1.5 rounded-full transition-all ${
                          isActive ? 'w-4 bg-blue-500' : isCompleted ? 'w-1.5 bg-emerald-500' : 'w-1.5 bg-gray-300 dark:bg-gray-600'
                        }`}
                        title={item.content?.title}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-400">No items in this trail</p>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Buttons - Add item mode for trail detail */}
      <FloatingActionButtons 
        showAddItemOnly={true}
        onAddItem={handleOpenAddItemModal}
        onDiscover={() => setShowDiscoverModal(true)}
      />

      {/* Discover Modal */}
      <DiscoverModal
        isOpen={showDiscoverModal}
        onClose={() => {
          setShowDiscoverModal(false);
          loadPathDetails(); // Refresh path data when modal closes
        }}
        context="learning-path-detail"
        contextData={{
          pathId: pathId,
          itemId: currentItem?.id // Use trail item ID, not content ID
        }}
      />

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add Items to Trail
              </h2>
              <button
                onClick={() => setShowAddItemModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X weight="bold" className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search your library..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-100 dark:bg-gray-900 border-0 rounded-lg
                           text-gray-900 dark:text-white placeholder:text-gray-400
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4">
              {libraryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_infinite]" />
                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
                    <span className="w-2 h-2 rounded-full bg-blue-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              ) : filteredLibraryItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {searchQuery ? 'No items match your search' : 'No items available to add'}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredLibraryItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleItemSelection(item.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        selectedItems.has(item.id)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedItems.has(item.id)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selectedItems.has(item.id) && (
                          <CheckCircle weight="fill" className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {item.category} • {item.reading_time_minutes || 10} min
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddItemModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                           hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddItemsToPath}
                  disabled={selectedItems.size === 0 || isAddingItems}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 
                           disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors
                           flex items-center gap-2"
                >
                  {isAddingItems ? (
                    <>
                      <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} /></span>
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus weight="bold" className="w-4 h-4" />
                      Add to Trail
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
